import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  auth,
  onAuthStateChanged,
  completeGoogleRedirectIfPresent,
  isAllowed,
  signOut,
  type User,
} from '../firebase'

interface AuthState {
  user: User | null
  loading: boolean
  /** Set when getRedirectResult fails (e.g. OAuth misconfig). */
  redirectError: string | null
}

const Ctx = createContext<AuthState>({ user: null, loading: true, redirectError: null })

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    redirectError: null,
  })

  useEffect(() => {
    let unsub = () => {}
    let cancelled = false

    ;(async () => {
      try {
        const redirected = await completeGoogleRedirectIfPresent()
        if (cancelled) return
        if (redirected && !isAllowed(redirected)) {
          await signOut()
          if (!cancelled) {
            setState({
              user: null,
              loading: false,
              redirectError:
                'Den här e-postadressen har inte tillgång till dashboarden.',
            })
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Inloggning via redirect misslyckades'
          setState((s) => ({ ...s, redirectError: msg }))
        }
      }

      unsub = onAuthStateChanged(auth, (user) => {
        if (cancelled) return
        setState((s) => ({
          user,
          loading: false,
          // Clear redirect error once a user session exists
          redirectError: user ? null : s.redirectError,
        }))
      })
    })()

    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>
}

export function useAuth() {
  return useContext(Ctx)
}
